import { useEffect, useRef, useState } from 'react'
import { searchPages } from '@/api/wiki'
import type { WikiPage } from '@/types/wiki'

interface Props {
  onClose: () => void
  onNavigate: (slug: string) => void
}

export default function SearchModal({ onClose, onNavigate }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<WikiPage[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      try {
        const data = await searchPages(query.trim())
        setResults(data)
        setSelectedIdx(0)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 250)

    return () => clearTimeout(timerRef.current)
  }, [query])

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      onNavigate(results[selectedIdx].slug)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-calamity-panel-dark border border-calamity-accent/30 rounded-2xl shadow-glow-lg overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-calamity-border-subtle">
          <svg className="w-5 h-5 text-calamity-accent shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search wiki pages..."
            className="flex-1 bg-transparent text-calamity-text placeholder-calamity-text-muted outline-none text-sm"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-calamity-panel border border-calamity-border-subtle text-calamity-text-muted">
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">Searching...</div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">No results found</div>
          )}

          {!loading && results.map((page, idx) => (
            <button
              key={page.id}
              onClick={() => onNavigate(page.slug)}
              className={`
                w-full text-left px-4 py-3 flex items-center gap-3 transition-colors
                ${idx === selectedIdx ? 'bg-calamity-accent/10' : 'hover:bg-calamity-panel-hover'}
              `}
            >
              <span className="text-lg shrink-0">{page.icon || '\uD83D\uDCC4'}</span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-calamity-text truncate">{page.title}</div>
                {page.category_name && (
                  <div className="text-xs text-calamity-text-muted">{page.category_name}</div>
                )}
              </div>
            </button>
          ))}

          {!loading && query.length < 2 && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
